import React, { useEffect, useRef, useState } from 'react';
import { Chart as ChartJS, TimeScale, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import 'chartjs-adapter-moment';
import annotationPlugin from 'chartjs-plugin-annotation';

const config = require('./config');
const TempChart = ({ curDate, elspotDataPoints, measurementsToShowInChart }) => {
  ChartJS.register(TimeScale, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, annotationPlugin);

  const curDateStart = moment(curDate).startOf('day');
  
  const [weatherForecastDataPoints, setWeatherForecastDataPoints] = useState([]);
  const [measurementDataPoints, setMeasurementDataPoints] = useState([]);

  const token = localStorage.getItem('token');

  const chartLineColors = [
    'rgba(255, 99, 132, 0.2)',  // Red
    'rgba(54, 162, 235, 0.2)',  // Blue
    'rgba(255, 206, 86, 0.2)',  // Yellow
    'rgba(75, 192, 192, 0.2)',  // Green
    'rgba(153, 102, 255, 0.2)', // Purple
    'rgba(255, 159, 64, 0.2)',  // Orange
    'rgba(128, 128, 0, 0.2)',   // Olive
    'rgba(0, 128, 0, 0.2)',     // Dark Green
    'rgba(128, 0, 128, 0.2)',   // Purple
    'rgba(0, 128, 128, 0.2)',   // Teal
    'rgba(0, 0, 128, 0.2)',     // Navy
  ];

  const mapHeader = (header, type) => {
    if (type === 'Temp')
      return header;
    else if (type === 'Humidity')
      return header.replace('C°', '%');
    else if (type === 'Voltage')
      return header.replace('C°', 'V');
    else
      return header;
  }

  const data = {
    labels: Array.from({length: 24}, (_, i) => ''), // Hours   moment().startOf('day').add(i, 'hours')
    datasets: [
      {
        label: 'Elspot (c/KWh)',
        data: elspotDataPoints,
        fill: false,
        yAxisID: 'y',
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Weather Forecast (°C)',
        data: weatherForecastDataPoints,
        yAxisID: 'y1',
        borderColor: 'rgba(0, 255, 0, 0.2)'
      },
      ...measurementsToShowInChart.filter(m => m.type === 'Temp' || m.type === 'Humidity' || m.type === 'Voltage').map((m, index) => ({
        label: mapHeader(m.header, m.type),
        data: measurementDataPoints.find(point => point.measurement === m.measurement)?.data,
        yAxisID: 'y1',
        borderColor: chartLineColors[index],
        pointStyle: m.showChartPoints ? 'circle' : false,
      })),
      ...measurementsToShowInChart.filter(m => m.type === 'electricity').map((m, index) => ({
        label: m.header,
        data: measurementDataPoints.find(point => point.measurement === m.measurement)?.data,
        yAxisID: 'y2',
        borderColor: chartLineColors[index+6],
        pointStyle: m.showChartPoints ? 'circle' : false,
      })),
    ]
  };

  const options = {
    animation: false,
    parsing: false,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {        
        callbacks: {
          label: function(context) {          
            if (context.dataset.yAxisID === 'y') {
              return  `Elspot: ${context.parsed.y.toFixed(2)} c/kWh`;
            } else if (context.dataset.yAxisID === 'y1') {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}`;
            }
          }
        }
      },
      legend: {
        display: true,
        position: 'top'
      },
      annotation: {
        annotations: {
          line1: {
            type: 'line',
            xMin: moment().startOf('minute').subtract(moment().minute() % 15, 'minutes'), // The line should be on the nearest quarter
            xMax: moment().startOf('minute').subtract(moment().minute() % 15, 'minutes'),
            borderColor: 'rgb(255, 0, 0)',
            borderWidth: 2,
          },
        }
      }
    },    
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day'
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          // callback: function(value, index, values) {
          //   return '';  // Hide x-axis labels
          // }
        },
        min: moment(curDateStart),
        max: moment(curDateStart).add(24, 'hours')
      },
      y: {
        suggestedMax: 20,
        beginAtZero: true
      },
      y1: {
        position: 'right',        
      },
      y2: measurementsToShowInChart.filter(m => m.type === 'electricity').length > 0 ? {
          position: 'right'
        } :
        {
          display: false
        }
    }
  };

  const chartRef = useRef(null);

  const fetchData = () => {
    const curDateStart = moment(curDate).startOf('day');      
    
    const chart = chartRef.current;
    if (chart) {
      chart.options.scales.x.min = curDateStart;
      chart.options.scales.x.max = moment(curDateStart).add(23, 'hours');
      chart.update();
    }      

    if (moment(curDate).isSameOrAfter(moment(), 'day')) {
      fetch(config.serverUrl + '/measurements?measurement=weatherforecast&date=' + moment(curDate).format('YYYY-MM-DD'),  {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => response.json())
      .then(data => {
        let values = [];
        if (data[0] != null)
          values = data[0].map((point, index) => {
            return {
              x: moment(curDateStart).add(index, 'hours').toDate(),
              y: point.temperature
            };
          });

          setWeatherForecastDataPoints(values);
      })
      .catch(error => { console.log(error); });
    }
    else
      setWeatherForecastDataPoints([]);

    // if (chartRef && chartRef.current && chartRef.current.chartInstance) {
    //   const chartInstance = chartRef.current.chartInstance;
    //   chartInstance.destroy();
    // }
  };

  useEffect(() => {  
    fetchData();
  }, [curDate, token]);

  const fetchMeasuremmentData = () => {
    setMeasurementDataPoints([]);

    measurementsToShowInChart.forEach(m => {
      fetch(config.serverUrl + `/measurements?measurement=${m.measurement}&date=${moment(curDate).format('YYYY-MM-DD')}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        .then(response => response.json())
        .then(data => {
          let values = data[0].map((point, index) => {
            return {
              x: moment(point.time).toDate(),
              y: point.value
            };
          });
  
          setMeasurementDataPoints(oldDataPoints => [...oldDataPoints, { measurement: m.measurement, data: values }]);
        })
        .catch(error => { console.log(error); });
    });
  };

  // Doesn't work
  // useEffect(() => {
  //   fetchMeasuremmentData(); // Initial fetch
  
  //   const interval = setInterval(() => {
  //     fetchMeasuremmentData();
  //   }, 30000); // Fetch every 30 seconds

  //   return () => clearInterval(interval); // Cleanup interval on component unmount
  // }, []);  

  useEffect(() => {
    fetchMeasuremmentData();    
  }, [curDate, measurementsToShowInChart, token]);
  
  return (
    <>
      <Line ref={chartRef} data={data} options={options} />
    </>
  );
}

export default TempChart;